import Module from '~/lib/vue/modules/module';

export default (services, namespace='playbook') => {

  let api = services.get('api');
  let notifier = services.get('notifier');

  let notify = {
    created: (label) => ({type: 'success', title: 'Created', text: label}),
    saved: (label) => ({type: 'success', title: 'Saved', text: label}),
    deleted: (label) => ({type: 'warning', title: 'Deleted', text: label}),
    failed: (e) => ({type: 'error', title: 'Something went wrong', text: e.message || 'Unknown error'}),
  };

  return Module.create({
    namespace,
    services,

    state: {
      playbooks: [],
      loading: false,
      errors: {},
    },

    getters: {
      playbooks: state => state.playbooks,
      loading: state => state.loading,
      errors: state => state.errors,
    },

    mutations: {
      playbooks: (state, playbooks) => state.playbooks = playbooks,
      loading: (state, loading) => state.loading = loading,
      errors: (state, errors) => state.errors = errors,
    },

    actions: {

      initialize: async (store) => {
        store.commit('playbooks', []);
        store.commit('loading', false);
        store.commit('errors', {});
      },

      load: async (store) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let response = await api.get('/playbooks');
          let playbooks = response.payload || [];
          store.commit('playbooks', playbooks);
        }
        catch (e) {
          notifier.notify(notify.failed(e));
        }
        finally {
          store.commit('loading', false);
        }
      },

      createPlaybook: async (store, {name}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({name});
          await api.post('/playbooks', {body});
          notifier.notify(notify.created(name));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      updatePlaybook: async (store, {id, name}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({name});
          await api.put(`/playbooks/${id}`, {body});
          notifier.notify(notify.saved(name));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      deletePlaybook: async (store, {id}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          await api.delete(`/playbooks/${id}`);
          notifier.notify(notify.deleted('Playbook'));
          await store.dispatch('load');
        }
        catch (e) {
          notifier.notify(notify.failed(e));
          store.commit('loading', false);
        }
      },

      createBucket: async (store, {playbookId, slug, label, sort_order}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({slug, label, sort_order});
          await api.post(`/playbooks/${playbookId}/buckets`, {body});
          notifier.notify(notify.created(label));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      updateBucket: async (store, {playbookId, bucketId, slug, label, sort_order}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({slug, label, sort_order});
          await api.put(`/playbooks/${playbookId}/buckets/${bucketId}`, {body});
          notifier.notify(notify.saved(label));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      deleteBucket: async (store, {playbookId, bucketId}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          await api.delete(`/playbooks/${playbookId}/buckets/${bucketId}`);
          notifier.notify(notify.deleted('Bucket'));
          await store.dispatch('load');
        }
        catch (e) {
          notifier.notify(notify.failed(e));
          store.commit('loading', false);
        }
      },

      createPlay: async (store, {playbookId, bucketId, label, items, sort_order}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({label, items, sort_order});
          await api.post(`/playbooks/${playbookId}/buckets/${bucketId}/plays`, {body});
          notifier.notify(notify.created(label));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      updatePlay: async (store, {playbookId, bucketId, playId, label, items, sort_order}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          let body = JSON.stringify({label, items, sort_order});
          await api.put(`/playbooks/${playbookId}/buckets/${bucketId}/plays/${playId}`, {body});
          notifier.notify(notify.saved(label));
          await store.dispatch('load');
        }
        catch (e) {
          if (e.response && e.response.payload && e.response.payload.errors) {
            store.commit('errors', e.response.payload.errors);
          } else {
            notifier.notify(notify.failed(e));
          }
          store.commit('loading', false);
        }
      },

      deletePlay: async (store, {playbookId, bucketId, playId}) => {
        store.commit('loading', true);
        store.commit('errors', {});

        try {
          await api.delete(`/playbooks/${playbookId}/buckets/${bucketId}/plays/${playId}`);
          notifier.notify(notify.deleted('Play'));
          await store.dispatch('load');
        }
        catch (e) {
          notifier.notify(notify.failed(e));
          store.commit('loading', false);
        }
      },

    },
  });
};
